import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage } from './pages/LoginPage';
import ValueReport from './pages/ValueReport';
import { PortCoProvider, usePortCo } from './context/PortCoContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ExecutiveDashboard } from './pages/ExecutiveDashboard';
import { WorkstreamDetail } from './pages/WorkstreamDetail';
import { IntegrationHub } from './pages/IntegrationHub';
import { OnboardingWizard } from './pages/OnboardingWizard';
import { SettingsPage } from './pages/Settings';
import { AgentFleetPage } from './pages/AgentFleetPage';
import { EntitlementsPage } from './pages/Entitlements';
import { ActionQueue } from './components/ActionQueue';
import { ActionDetail } from './components/ActionDetail';
import { AuditLog } from './components/AuditLog';
import { StatsBar } from './components/StatsBar';
import { rcmApi } from './api/rcmApi';
import {
  getPendingActions,
  downloadActionArtifact,
  getActionDetail,
  approveAction,
  rejectAction,
  ageOutPendingActions,
} from './api/client';
import type { PendingAction, StatsSummary } from './types';

// Wrapper for Approvals page — wired to live /api/actions endpoint (all 9 workstreams)
const ApprovalsPage: React.FC = () => {
  const { selectedPortco } = usePortCo();
  const [actions, setActions] = React.useState<PendingAction[]>([]);
  const [totalPending, setTotalPending] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [cleanupBusy, setCleanupBusy] = React.useState(false);
  const [queueView, setQueueView] = React.useState<'pending' | 'approved'>('pending');
  const [cleanupMsg, setCleanupMsg] = React.useState<string | null>(null);
  const [liveStats, setLiveStats] = React.useState<StatsSummary | null>(null);

  // ActionDetail modal state
  const [selectedAction, setSelectedAction] = React.useState<PendingAction | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new /api/actions endpoint — returns ALL workstreams, not just RCM
      // Filtered by the global PortCo selector (server-side when possible)
      const { actions: pendingActions, total } = await getPendingActions(selectedPortco, queueView);

      setActions(pendingActions);
      setTotalPending(total);

      // Build live stats from real API data
      const pendingCount = pendingActions.length;

      // Group by workstream for stats
      const workstreamSet = new Set(pendingActions.map(a => a.workstream));
      const activeAgents = workstreamSet.size;

      // Fetch dashboard for savings data
      let monthlySavings = '$0';
      let complianceScore = '—';
      try {
        const dashboardRes = await rcmApi.getDashboard(selectedPortco);
        const agentRunsCount = dashboardRes?.metrics?.agentRunsCount ?? 0;
        const recoveredAmount = dashboardRes?.metrics?.totalDenialAmount ?? 0;
        const automationHoursSaved = agentRunsCount * 4.5;
        const laborRatePerHour = 45;
        const totalSavings = recoveredAmount + (automationHoursSaved * laborRatePerHour);
        monthlySavings = totalSavings > 0 ? `$${Math.round(totalSavings / 1000)}K` : '$0';

        const appealSuccess = dashboardRes?.metrics?.successRate ?? 0;
        const workstreamCoverage = (activeAgents / 9) * 100;
        const humanReviewRate = 100;
        complianceScore = `${Math.round(
          (appealSuccess * 0.4) + (workstreamCoverage * 0.3) + (humanReviewRate * 0.3)
        )}%`;
      } catch {
        // Dashboard fetch failed — use basic stats
      }

      setLiveStats({
        activeAgents,
        pendingCount,
        monthlySavings,
        complianceScore,
      });
    } catch (err) {
      console.error('Error loading actions:', err);
      setActions([]);
      setTotalPending(0);
      setLiveStats({ activeAgents: 0, pendingCount: 0, monthlySavings: '$0', complianceScore: '—' });
      setError('Failed to load pending actions. Check API connection.');
    } finally {
      setLoading(false);
    }
  }, [selectedPortco, queueView]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Open detail modal and fetch full action data from /api/actions/{id}
  const handleSelectAction = React.useCallback(async (action: PendingAction) => {
    setSelectedAction(action);
    setDetailOpen(true);
    setDetailLoading(true);

    // Fetch full detail from backend (includes sourceData, auditTrail, etc.)
    try {
      const fullDetail = await getActionDetail(action.actionId);
      if (fullDetail) {
        setSelectedAction(fullDetail);
      }
    } catch (err) {
      console.warn('Could not fetch full detail, using list data:', err);
      // Keep the partial data from the list
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCloseDetail = React.useCallback(() => {
    setDetailOpen(false);
    setSelectedAction(null);
  }, []);

  const handleApprove = async (id: string) => {
    try {
      await approveAction(id, 'Viral Shah');
      await fetchData();
    } catch (err) {
      console.error('Approve failed:', err);
    }
  };

  const handleReject = async (id: string, reason?: string) => {
    try {
      await rejectAction(id, 'Viral Shah', reason || 'Rejected by reviewer');
      await fetchData();
    } catch (err) {
      console.error('Reject failed:', err);
    }
  };

  const handleBatchApprove = async (ids: string[]) => {
    for (const id of ids) {
      try {
        await approveAction(id, 'Viral Shah');
      } catch (err) {
        console.error(`Batch approve failed for ${id}:`, err);
      }
    }
    await fetchData();
  };

  const handleBatchReject = async (ids: string[], reason?: string) => {
    for (const id of ids) {
      try {
        await rejectAction(id, 'Viral Shah', reason || 'Batch rejected');
      } catch (err) {
        console.error(`Batch reject failed for ${id}:`, err);
      }
    }
    await fetchData();
  };

  // Age-out policy: expire stale low-risk items past the cutoff. The action
  // is withdrawn, never executed — HITL-safe cleanup of queue noise.
  const handleAgeOut = async () => {
    setCleanupBusy(true);
    setCleanupMsg('Checking for stale low-risk items...');
    try {
      const preview = await ageOutPendingActions({ portcoId: selectedPortco, dryRun: true });
      if (!preview.eligible) {
        setCleanupMsg('Nothing to clean up — no low-risk items older than 14 days.');
        return;
      }
      const ok = window.confirm(
        `Expire ${preview.eligible.toLocaleString()} low-risk pending action(s) that have sat in the review queue for more than 14 days?\n\nThese actions were never executed — they will be withdrawn and marked expired with an audit trail entry.`
      );
      if (!ok) {
        setCleanupMsg('Cleanup cancelled.');
        return;
      }
      let total = 0;
      for (let i = 0; i < 10; i++) {
        const res = await ageOutPendingActions({ portcoId: selectedPortco });
        total += res.resolved;
        setCleanupMsg(`Expired ${total.toLocaleString()} stale item(s)${res.remaining > 0 ? ` — ${res.remaining.toLocaleString()} remaining...` : ''}`);
        if (res.remaining === 0) break;
      }
      await fetchData();
      setCleanupMsg(`Done — ${total.toLocaleString()} stale low-risk item(s) expired. They were never executed.`);
    } catch (err) {
      console.error('Age-out failed:', err);
      setCleanupMsg('Cleanup failed — check API connection.');
    } finally {
      setCleanupBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-1">Human-in-the-Loop Approval Queue</h1>
            <p className="text-xs text-slate-400">Review and approve AI agent actions before execution</p>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg border border-slate-200 bg-white ml-auto mr-3">
            {(['pending', 'approved'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setQueueView(v)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  queueView === v ? 'bg-mantis-600 text-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {v === 'pending' ? 'Pending' : 'Approved · Packets'}
              </button>
            ))}
          </div>
          <button
            onClick={handleAgeOut}
            disabled={cleanupBusy || loading}
            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Expire low-risk pending actions older than 14 days. Actions are withdrawn, never executed."
          >
            {cleanupBusy ? 'Working...' : 'Clean up stale items'}
          </button>
        </div>
        {cleanupMsg && (
          <p className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
            {cleanupMsg}
          </p>
        )}
        {error && (
          <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            {error}
          </p>
        )}
      </div>
      <StatsBar stats={liveStats} loading={loading} />
      <ActionQueue
        actions={actions}
        totalPending={totalPending}
        loading={loading}
        view={queueView}
        onDownloadPacket={downloadActionArtifact}
        onApprove={handleApprove}
        onReject={handleReject}
        onBatchApprove={handleBatchApprove}
        onBatchReject={handleBatchReject}
        onSelectAction={handleSelectAction}
      />

      {/* Action Detail Modal — shows full context for informed decision-making */}
      <ActionDetail
        action={selectedAction}
        isOpen={detailOpen}
        onClose={handleCloseDetail}
        onDownloadPacket={downloadActionArtifact}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      {/* Loading overlay for detail fetch */}
      {detailLoading && detailOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-500/20 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 shadow-xl flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-mantis-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-medium text-slate-700">Loading action details…</span>
          </div>
        </div>
      )}
    </div>
  );
};

const AuditPage: React.FC = () => (
  <div>
    <div className="mb-6">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Governance & Audit Trail</h1>
      <p className="text-xs text-slate-400">Immutable log of every human approval, rejection, and automated agent action</p>
    </div>
    <AuditLog />
  </div>
);

// Auth gate: shows login until a session is established
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center font-sans">
        <div className="text-xs text-slate-400 font-medium">Loading Meridian…</div>
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AuthGate>
          <HashRouter>
            <PortCoProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<ExecutiveDashboard />} />
              <Route path="/workstreams" element={<WorkstreamDetail />} />
              <Route path="/workstreams/:workstreamId" element={<WorkstreamDetail />} />
              <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />
              <Route path="/onboarding" element={<OnboardingWizard />} />
              <Route path="/approvals" element={<ApprovalsPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/value-report" element={<ValueReport />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/integrations" element={<IntegrationHub />} />
              <Route path="/settings/agent-fleet" element={<AgentFleetPage />} />
            <Route path="/entitlements" element={<EntitlementsPage />} />
            </Routes>
            </Layout>
          </PortCoProvider>
        </HashRouter>
        </AuthGate>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
